import React, { FC, memo, useMemo } from 'react';
import { backgroundImageForUrl } from '@shared/utils/style/backgroundImage';
import { Box, Flex } from '@withjoy/joykit';
import { addRendition } from '@shared/utils/photoRendition';
import { pxToRem } from '@withjoy/joykit/theme';
import { LazyScreen } from '../LazyScreen/LazyScreen';
import { AnimatedImageBox } from '../PhotoTrio/PhotoTrio.styles';
import { CroppedImage } from '@shared/components/MultiPhotoSelector/components/CroppedImage/CroppedImage';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import type { ArchPhotoProps, PhotoArchProps } from '../PhotoArch/PhotoArch.types';
import { useSizeObserver } from '@shared/utils/hooks/useSizeObserver';
import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';

const ASPECT_RATIO = typeof PHOTO_PRESENTATION_CONFIG.videoArch.aspectRatio === 'number' ? PHOTO_PRESENTATION_CONFIG.videoArch.aspectRatio : 6 / 5;
const OUTER_PIECE_THICKNESS = 2.88;
const INNER_GAP_THICKNESS_Y = 35;
const INNER_GAP_THICKNESS_X = 29;
// Padding to hide the top portion of the arch behind the video on desktop
// Value empirically determined to match design spec - creates semi-circle appearance
const ARCH_TOP_PADDING_DESKTOP = 200;

// Will keep this as a separate since the arch logic is different for video. The video uses an outer trasnparent arch.
const ArchPhoto: FC<ArchPhotoProps> = props => {
  const { photo, containerDimensions, calculateBorderRadius, outerArchColor, innerArchColor, isMobileScreen } = props;
  if (photo.cropInfo) {
    let height = 0;
    let width = 0;
    if (isMobileScreen) {
      height = containerDimensions.height;
      width = height * ASPECT_RATIO;
    } else {
      width = containerDimensions.width;
      height = width / ASPECT_RATIO;
    }
    return (
      <Box
        position="relative"
        width={isMobileScreen ? 'auto' : '100%'}
        height={isMobileScreen ? '100%' : 'auto'}
        borderTopLeftRadius={pxToRem(calculateBorderRadius)}
        borderTopRightRadius={pxToRem(calculateBorderRadius)}
        aspectRatio={`${ASPECT_RATIO}`}
        border={`${pxToRem(OUTER_PIECE_THICKNESS)} solid`}
        borderColor={outerArchColor}
        paddingTop={pxToRem(INNER_GAP_THICKNESS_X)}
        paddingX={pxToRem(INNER_GAP_THICKNESS_Y)}
        backgroundColor={innerArchColor}
        borderBottom="none"
        boxSizing="border-box"
      >
        <AnimatedImageBox
          display="flex"
          position="relative"
          width="100%"
          height={isMobileScreen ? '100%' : 'auto'}
          borderTopLeftRadius={pxToRem(calculateBorderRadius)}
          borderTopRightRadius={pxToRem(calculateBorderRadius)}
          aspectRatio={isMobileScreen ? 'auto' : `${ASPECT_RATIO}`}
          overflow="hidden"
        >
          <Box
            position="absolute"
            inset={0}
            width="100%"
            height="100%"
          >
            <CroppedImage
              cropInfo={{ ...photo.cropInfo, unit: '%' }}
              imageUrl={addRendition({ url: photo.url, renditionSize: 'medium' })}
              height={height}
              width={width}
            />
          </Box>
        </AnimatedImageBox>
      </Box>
    );
  }
  return (
    <Box
      position="relative"
      width={isMobileScreen ? 'auto' : '100%'}
      height={isMobileScreen ? '100%' : 'auto'}
      borderTopLeftRadius={pxToRem(calculateBorderRadius)}
      borderTopRightRadius={pxToRem(calculateBorderRadius)}
      aspectRatio={`${ASPECT_RATIO}`}
      border={`${pxToRem(OUTER_PIECE_THICKNESS)} solid`}
      borderColor={outerArchColor}
      backgroundColor={innerArchColor}
      borderBottom="none"
    >
      <AnimatedImageBox
        position="relative"
        width="100%"
        height={isMobileScreen ? '100%' : 'auto'}
        borderTopLeftRadius={pxToRem(calculateBorderRadius)}
        borderTopRightRadius={pxToRem(calculateBorderRadius)}
        aspectRatio={isMobileScreen ? 'auto' : `${ASPECT_RATIO}`}
        backgroundImage={backgroundImageForUrl(addRendition({ url: photo.url, renditionSize: 'medium' }))}
        backgroundSize="cover"
        backgroundRepeat="no-repeat"
        backgroundPosition="center"
        borderBottom="none"
      />
    </Box>
  );
};

export const VideoArchPhoto: FC<PhotoArchProps> = memo(props => {
  const { photo, showSkeleton, innerArchColor = 'white', outerArchColor = 'mono14', showPlaceholder = false } = props;

  const [containerDimensions, containerRef] = useSizeObserver();
  const isMobileScreen = useIsMobileScreen();

  // The height calculations are based on the width of the container.
  const calculateBorderRadius = useMemo(() => {
    if (isMobileScreen) {
      const width = containerDimensions.height * ASPECT_RATIO;
      return width / 2 + OUTER_PIECE_THICKNESS;
    } else {
      const width = containerDimensions.width;
      return width / 2 + OUTER_PIECE_THICKNESS;
    }
  }, [containerDimensions.width, containerDimensions.height, isMobileScreen]);

  const skeletonRootProps = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      aspectRatio: `${ASPECT_RATIO}`,
      position: isMobileScreen ? 'absolute' : 'relative',
      display: 'flex',
      justifyContent: 'center',
    }),
    [isMobileScreen],
  );

  const containerHeight = useMemo(() => {
    if (isMobileScreen) {
      return '100%';
    } else {
      return pxToRem(containerDimensions.width / ASPECT_RATIO - ARCH_TOP_PADDING_DESKTOP);
    }
  }, [containerDimensions.width, isMobileScreen]);

  return (
    <Flex
      width="100%"
      height={containerHeight}
      aspectRatio={`${ASPECT_RATIO}`}
      alignItems={isMobileScreen ? 'center' : 'flex-end'}
      justifyContent={isMobileScreen ? 'center' : 'stretch'}
      overflow="hidden"
      ref={containerRef}
    >
      {!photo ? (
        showPlaceholder ? (
          <Box
            width="80%"
            height="100%"
            backgroundColor="lightgray"
            borderTopLeftRadius={pxToRem(calculateBorderRadius)}
            borderTopRightRadius={pxToRem(calculateBorderRadius)}
          />
        ) : null
      ) : (
        <LazyScreen
          key={photo.id}
          src={addRendition({ url: photo.url, renditionSize: 'medium' })}
          skeletonGroupOverrides={{
            Root: {
              props: skeletonRootProps,
            },
            PlaceholderRoot: {
              props: skeletonRootProps,
            },
          }}
          width="100%"
          height="100%"
          aspectRatio={`${ASPECT_RATIO}`}
          showSkeleton={showSkeleton}
        >
          {() => (
            <ArchPhoto
              photo={photo}
              containerDimensions={containerDimensions}
              calculateBorderRadius={calculateBorderRadius}
              outerArchColor={outerArchColor}
              innerArchColor={innerArchColor}
              isMobileScreen={isMobileScreen}
            />
          )}
        </LazyScreen>
      )}
    </Flex>
  );
});

VideoArchPhoto.displayName = 'VideoArchPhoto';
